import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/styles';
import BankingService from 'services/banking.service';
import { useToasts } from 'hooks/toasts';
import ManualWithdrawForm from 'views/dashboard/private/ManualWithdraw/components/ManualWithdrawForm';
import { IWithdrawalRequest } from 'common/withdrawal-request.interface';
import { EAccountStatus, EAccountType, IAccount } from 'common/accounts.interface';
import { ICounterparty } from 'common/secondary-operation.interface';
import ManualWithdrawTable from './components/ManualWithdrawTable';
import WithdrawalReviewModal from './modals/WithdrawalReviewModal';
import ManualWithdrawFilters from './components/ManualWithdrawFilters';

type WithdrawalFilters = {
  requestStatus?: string;
  transactionStatus?: string;
  originAccountId?: number;
  counterpartyId?: number;
  createdAtFrom?: string;
  createdAtTo?: string;
};

const ManualWithdraw = () => {
  const theme = useTheme<any>();
  const { triggerToast } = useToasts();

  const [open, setOpen] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IWithdrawalRequest | undefined>(undefined);
  const [viewSource, setViewSource] = useState<'status' | 'eye' | null>(null);

  const [filters, setFilters] = useState<WithdrawalFilters>({});

  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [counterparties, setCounterparties] = useState<ICounterparty[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rows, setRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const bankingService = useMemo(() => new BankingService(), []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenView = (row?: IWithdrawalRequest, source: 'status' | 'eye' = 'eye') => {
    if (row) setSelectedRow(row);
    setViewSource(source);
    setOpenView(true);
  };

  const handleCloseView = () => {
    setOpenView(false);
    setSelectedRow(undefined);
    setViewSource(null);
  };

  useEffect(() => {
    setLoadingFilters(true);

    Promise.all([
      bankingService.getAccounts({
        status: EAccountStatus.ACTIVE,
        accountType: EAccountType.CONTROL,
      }),
      bankingService.getCounterpartyBanking(),
    ])
      .then(([accs, cps]) => {
        setAccounts(accs || []);
        setCounterparties(cps || []);
      })
      .finally(() => setLoadingFilters(false));
  }, [bankingService]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;

      const res = await bankingService.getWithdrawalRequests({
        page,
        limit,
        ...filters,
      });

      setRows(res.items);
      setRowCount(res.total);
    } catch (e) {
      triggerToast('Erro ao carregar solicitações de retirada.', {
        variant: 'error',
      });
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [bankingService, paginationModel.page, paginationModel.pageSize, filters, triggerToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container spacing={2} sx={{ pt: '24px', pl: '20px', pr: 2, pb: 2 }}>
      <Grid item xs={12} sx={{ mb: 1 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                fontSize: '24px',
                fontWeight: '700',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              Retirada manual | Contas de Recebimento
            </Typography>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '700',
                width: '200px',
              }}
              onClick={handleOpen}
            >
              Incluir transferência
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <ManualWithdrawFilters
          onApply={(f: WithdrawalFilters) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setFilters(f);
          }}
          accounts={accounts.map((a) => ({
            id: a.id,
            businessName: a.businessName,
            cnpj: a.cnpj,
          }))}
          counterparties={counterparties.map((c) => ({
            id: c.id,
            businessName: c.companyName,
            cnpj: c.documentNumber,
          }))}
          loadingFilters={loadingFilters}
        />
      </Grid>

      <Grid item xs={12}>
        <ManualWithdrawTable
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onOpenView={(row, source) => handleOpenView(row, source)}
        />
      </Grid>

      <ManualWithdrawForm
        open={open}
        onClose={handleClose}
        onSubmitted={fetchData}
        accounts={accounts}
        counterparties={counterparties}
        loadingFilters={loadingFilters}
      />

      <WithdrawalReviewModal
        open={openView}
        row={selectedRow}
        source={viewSource || 'eye'}
        onClose={handleCloseView}
        onRefresh={fetchData}
      />
    </Grid>
  );
};

export default ManualWithdraw;
